import { Collapse, Grid, List, ListItem, ListItemText, Typography } from "@mui/material"
import { GraphNodeData, GroupNodeData } from "@store/services/converter/types"
import { Node, useReactFlow, useStoreApi } from "reactflow"
import { getGroupedNodesForGroup, getNodeLabel } from "utils/graph"
import { GraphStoreType } from "@features/graph/Graph"
import { GraphDataViewModel } from "@features/graph/graphViewModel"
import { expandGroupNode } from "@features/graph/handlers/graphGroupCollapseExpand"
import CardContent from "@mui/material/CardContent"
import { ExpandLess } from "@mui/icons-material"
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight"
import Card from "@mui/material/Card"
import { useEffect, useMemo, useState } from "react"
import { graphNodeClickHandler } from "@features/graph/handlers/graphNodeClick"

type NodeGroupDetailsProps = {
  node: Node<GroupNodeData>
}

const NodeGroupDetails = ({ node }: NodeGroupDetailsProps) => {
  const [openNodeIds, setOpenNodeIds] = useState<string[]>([])
  const groupLabel = getNodeLabel(node)
  const groupedNodes = useMemo(() => getGroupedNodesForGroup(node), [node])
  const graphStore: GraphStoreType = useStoreApi()
  const { fitView, getNode } = useReactFlow()

  useEffect(() => {
    if (groupedNodes.length === 1) {
      // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
      setOpenNodeIds([groupedNodes[0][0]])
    } else {
      setOpenNodeIds([])
    }
  }, [groupedNodes])

  const handleItemClick = (node: GraphNodeData) => {
    const graphState = graphStore.getState()
    if (node.display.groups) {
      node.display.groups.forEach((groupId) => {
        const node = GraphDataViewModel.data?.groupMap[groupId]
        if (node && GraphDataViewModel.data) {
          expandGroupNode(node, GraphDataViewModel.data, graphStore)
        }
      })
      graphState.resetSelectedElements()
      graphState.unselectNodesAndEdges()
      // move it in down in call stack, so we are sure groups are expanded at the time of clicking that node
      setTimeout(() => graphNodeClickHandler(node.node.name, graphStore))

      // wait for reactflow state update
      setTimeout(() => {
        const n = getNode(node.node.name)
        if (n) {
          fitView({ nodes: [n], duration: 0, maxZoom: 2.5 })
        }
      }, 0)
    } else {
      graphState.resetSelectedElements()
      graphState.unselectNodesAndEdges()
      graphNodeClickHandler(node.node.name, graphStore)

      setTimeout(() => {
        const n = getNode(node.node.name)
        if (n) {
          fitView({ nodes: [n], duration: 0, maxZoom: 2.5 })
        }
      }, 0)
    }
  }

  const handleOpenClose = (nodeId: string) => {
    if (openNodeIds.includes(nodeId)) {
      setOpenNodeIds(openNodeIds.filter((id) => id !== nodeId))
    } else {
      setOpenNodeIds([...openNodeIds, nodeId])
    }
  }

  return (
    <Grid container direction="column" sx={{ p: 1 }}>
      {groupedNodes.length > 1 ? (
        <Grid item>
          <Typography variant="h6">
            Parent Group <strong>{groupLabel}</strong>
          </Typography>
        </Grid>
      ) : null}
      <Grid item>
        <List>
          {groupedNodes.sort().map(([id, nodes]) => (
            <ParentList
              key={id}
              open={openNodeIds.includes(id)}
              id={id}
              nodes={nodes}
              onItemClick={handleItemClick}
              onOpenClose={handleOpenClose}
            />
          ))}
        </List>
      </Grid>
    </Grid>
  )
}

type ParentListProps = {
  open: boolean
  id: string
  nodes: GraphNodeData[]
  onItemClick: (node: GraphNodeData) => void
  onOpenClose: (nodeId: string) => void
}

const ParentList = ({ open, id, nodes, onItemClick, onOpenClose }: ParentListProps) => {
  const groupNode = GraphDataViewModel.data?.groupMap[id]

  return (
    <>
      <Card sx={{ width: "100%", boxShadow: "none" }}>
        <CardContent
          sx={{
            paddingBottom: "5px !important",
            padding: "5px",
            mb: "0px",
            cursor: "pointer",
            ":hover": { backgroundColor: "#f7f7f7" },
            borderBottom: "1px solid #e7e7e7",
          }}
          onClick={() => onOpenClose(id)}
        >
          {!open ? (
            <KeyboardArrowRightIcon sx={{ verticalAlign: "middle" }} />
          ) : (
            <ExpandLess sx={{ verticalAlign: "middle" }} />
          )}
          <Typography variant="body1" component="span" sx={{ ml: 1 }}>
            <b>{groupNode?.group.label || ""}</b>
          </Typography>
        </CardContent>
        <Collapse in={open} timeout="auto" unmountOnExit>
          <CardContent
            sx={{
              paddingBottom: "5px !important",
              padding: "5px",
              mb: "0px",
              ":hover": { backgroundColor: "#f7f7f7" },
            }}
          >
            {nodes
              .sort((a, b) => {
                return a.display.label < b.display.label ? -1 : 1
              })
              .map((node, index) => (
                <ListItem
                  divider
                  key={node.node.name}
                  onClick={() => onItemClick(node)}
                  sx={{
                    cursor: "pointer",
                    "&:hover": {
                      backgroundColor: "rgba(225, 225, 225, 0.4)",
                    },
                  }}
                >
                  <ListItemText
                    primary={`${node.display.label}`}
                    primaryTypographyProps={{ variant: "body2" }}
                  />
                </ListItem>
              ))}
          </CardContent>
        </Collapse>
      </Card>
    </>
  )
}

export default NodeGroupDetails
